"use client";

import { motion } from "framer-motion";
import { useLang } from "@/contexts/language-context";
import { CreditCard, Check } from "lucide-react";

export default function LiveBarTabsCloseScroll() {
  const { t } = useLang();

  return (
    <section className="relative py-32 px-6 bg-[#0A0814] text-white overflow-hidden">
      <div
        className="absolute bottom-0 right-1/4 w-[600px] h-[600px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(244,114,182,0.15) 0%, transparent 65%)" }}
      />

      <div className="relative max-w-[1080px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16 max-w-[760px] mx-auto"
        >
          <div className="text-[11px] font-black tracking-widest uppercase mb-4" style={{ color: "#F472B6" }}>
            {t("Close the tab", "Cierre de tab")}
          </div>
          <h2 className="text-4xl md:text-[64px] font-black tracking-[-0.03em] leading-[1.02]">
            {t("Close with ", "Cierra con ")}
            <span className="text-white/30">{t("card, cash or split. Done.", "tarjeta, efectivo o split. Listo.")}</span>
          </h2>
          <p className="text-[16px] font-medium text-white/55 leading-relaxed mt-6">
            {t(
              "When they're ready, charge their card, take cash, or split the check across the group. Tip line, done. Closed tabs move to history with every item on record.",
              "Cuando estén listos, cobras su tarjeta, recibes efectivo o divides la cuenta entre el grupo. Línea de propina, fin. Las tabs cerradas pasan a historial con cada item registrado."
            )}
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, ease: [0.21, 0.47, 0.32, 0.98] }}
          className="bg-white/[0.04] backdrop-blur-xl border border-white/10 rounded-[28px] p-7 md:p-8 max-w-[560px] mx-auto"
        >
          <div className="text-[10px] font-black tracking-widest uppercase text-white/45 mb-2">
            {t("Settle tab — JAMES", "Cerrar tab — JAMES")}
          </div>

          {/* Card payment */}
          <div className="bg-white/[0.04] border border-white/10 rounded-[14px] p-4 mb-5 flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-[10px] flex items-center justify-center"
              style={{ backgroundColor: "rgba(124,58,237,0.2)" }}
            >
              <CreditCard className="w-5 h-5" style={{ color: "#C4B5FD" }} />
            </div>
            <div className="flex-1">
              <div className="text-[10px] font-bold uppercase tracking-wider text-white/45">
                {t("Card", "Tarjeta")}
              </div>
              <div className="text-[14px] font-black text-white tabular-nums">
                Visa •••• 4321
              </div>
            </div>
            <div className="text-[10px] font-bold uppercase tracking-wider" style={{ color: "#2ECC71" }}>
              {t("Charged at close", "Cobrada al cerrar")}
            </div>
          </div>

          {/* Line items summary */}
          <div className="space-y-2 mb-5 text-[13px]" style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}>
            <div className="flex justify-between text-white/65">
              <span>{t("Subtotal · 6 items", "Subtotal · 6 items")}</span>
              <span className="tabular-nums">$87.00</span>
            </div>
            <div className="flex justify-between text-white/65">
              <span>{t("Tax", "Impuesto")}</span>
              <span className="tabular-nums">$7.18</span>
            </div>
            {/* Tip line — highlighted */}
            <motion.div
              initial={{ backgroundColor: "rgba(244,114,182,0)" }}
              whileInView={{ backgroundColor: "rgba(244,114,182,0.10)" }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.5, delay: 0.5 }}
              className="flex justify-between font-bold rounded px-2 py-1 -mx-2"
              style={{ color: "#F472B6" }}
            >
              <span>{t("Tip (20%)", "Tip (20%)")}</span>
              <span className="tabular-nums">+$18.84</span>
            </motion.div>
          </div>

          {/* Tip chips */}
          <div className="mb-6">
            <div className="text-[9px] font-bold uppercase tracking-wider text-white/45 mb-2">
              {t("Tip", "Propina")}
            </div>
            <div className="grid grid-cols-4 gap-2">
              {["15%", "18%", "20%", "25%"].map((tip, i) => {
                const selected = tip === "20%";
                return (
                  <motion.div
                    key={tip}
                    initial={{ opacity: 0, y: 6 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.35, delay: 0.3 + 0.08 * i }}
                    className={`text-center py-2.5 rounded-[10px] text-[12px] font-black tabular-nums ${
                      selected ? "" : "bg-white/[0.04] border border-white/10 text-white/55"
                    }`}
                    style={
                      selected
                        ? {
                            background: "linear-gradient(135deg, #7C3AED 0%, #F472B6 100%)",
                            color: "white",
                          }
                        : undefined
                    }
                  >
                    {tip}
                  </motion.div>
                );
              })}
            </div>
          </div>

          {/* Final settled */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.5, delay: 0.9 }}
            className="rounded-[18px] p-5 text-white"
            style={{
              background: "linear-gradient(135deg, #7C3AED 0%, #F472B6 100%)",
              boxShadow: "0 20px 50px rgba(124,58,237,0.35)",
            }}
          >
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[10px] font-black tracking-widest uppercase text-white/75 mb-1">
                  {t("Settled", "Saldado")}
                </div>
                <div className="text-[32px] font-black tabular-nums leading-none">$113.02</div>
              </div>
              <motion.div
                initial={{ scale: 0.4, opacity: 0 }}
                whileInView={{ scale: 1, opacity: 1 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.4, delay: 1.1 }}
                className="w-12 h-12 rounded-full bg-white flex items-center justify-center"
              >
                <Check className="w-6 h-6" style={{ color: "#7C3AED" }} strokeWidth={3.5} />
              </motion.div>
            </div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-white/75 mt-2">
              {t("To Visa •••• 4321 · 1:08 AM", "A Visa •••• 4321 · 1:08 AM")}
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
